import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

/**
 * Global Security Hook
 * 
 * IMPORTANT SECURITY NOTES:
 * - DevTools cannot be fully blocked in browsers - this is deterrence-based
 * - Screenshots cannot be fully prevented - this is deterrence-based  
 * - Screen recording cannot be fully blocked - this is deterrence-based
 * - This system provides deterrence, not foolproof security
 */
const useGlobalSecurity = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    // Debug: Log the raw environment variable
    console.log('🐛 DEBUG - Raw env var:', process.env.REACT_APP_RESTRICT_USER_ACTIONS);
    
    // TEMPORARY: Force disable for testing
    const forceDisable = false; // Set to true to force disable restrictions
    
    // Check if restrictions are enabled via environment variable AND user is a student
    const restrictUserActions = forceDisable ? false : process.env.REACT_APP_RESTRICT_USER_ACTIONS === 'true';
    const isStudent = user?.role === 'student';

    console.log('🐛 DEBUG - Calculated values:', { 
      restrictUserActions, 
      isStudent, 
      userRole: user?.role,
      forceDisable
    });

    // Check if current path allows copy-paste
    const currentPath = location.pathname;
    const isAllowedPath = 
      currentPath === '/code-lab' || 
      currentPath.startsWith('/student/assessments/') && currentPath.endsWith('/submit')||
      currentPath === '/profile' ||
      currentPath === '/resume-builder';
    
    console.log('🐛 DEBUG - Path check:', { currentPath, isAllowedPath });

    if (!restrictUserActions || !isStudent) {
      if (!restrictUserActions) {
        if (forceDisable) {
          console.log('🔓 Global security restrictions are FORCE DISABLED (temp override)');
        } else {
          console.log('🔓 Global security restrictions are disabled (REACT_APP_RESTRICT_USER_ACTIONS is not "true")');
        }
      } else if (!isStudent) {
        console.log(`🔓 Security restrictions only apply to students. Current user role: "${user?.role}"`);
      }
      return;
    }

    // Skip only copy-paste restrictions if on allowed path, but keep other security measures including right-click
    if (isAllowedPath) {
      console.log(`🔓 Copy-paste restrictions disabled for allowed path: "${currentPath}", but right-click and other security measures remain active`);
    }

    console.log(`🔒 Applying global security restrictions for student user: "${user?.role}"`);

    // Disable right-click context menu globally (always blocked, even on allowed paths)
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable copy, paste, and cut globally (except on allowed paths)
    const handleCopy = (e) => {
      if (!isAllowedPath) {
        e.preventDefault();
        return false;
      }
    };

    const handlePaste = (e) => {
      if (!isAllowedPath) {
        e.preventDefault();
        return false;
      }
    };

    const handleCut = (e) => {
      if (!isAllowedPath) {
        e.preventDefault();
        return false;
      }
    };

    // Block keyboard shortcuts globally
    const handleKeyDown = (e) => {
      // F12 - DevTools
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }

      // Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C - DevTools shortcuts
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }

      // Ctrl+U - View source
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }

      // Ctrl+C, Ctrl+V, Ctrl+X - Copy, Paste, Cut (only block if not on allowed path)
      if (e.ctrlKey && (e.keyCode === 67 || e.keyCode === 86 || e.keyCode === 88)) {
        if (!isAllowedPath) {
          e.preventDefault();
          return false;
        }
      }

      // Windows+V - Clipboard History (only block if not on allowed path)
      if (e.metaKey && e.key === 'v') {
        if (!isAllowedPath) {
          console.log("Attempting to block Windows+V");
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }

      // Also try to prevent the Windows key from opening the start menu
      if (e.key === 'Meta' || e.keyCode === 91 || e.keyCode === 92) { // 91 for left Win, 92 for right Win
        console.log("Attempting to block Windows key");
        e.preventDefault();
        e.stopPropagation();
        
        // 🔥 TRIGGER VIOLATION for Windows key press
        console.log("🚨 WINDOWS KEY PRESSED - GLOBAL SECURITY VIOLATION");
        // Note: Global security doesn't have violation handling, but we log it
        return false;
      }

      return true;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [user, location]); // Add user and location dependency to re-run when user or route changes

  return null; // This hook doesn't return anything, it just applies restrictions
};

export default useGlobalSecurity;
